import * as React from 'react';
import { useCallback } from 'react';
import { Dialog, Autocomplete } from '@sravni/react-design-system';
import { Search } from '@sravni/react-icons';
import { useStyles, useDebounceValue, useFetch } from '@sravni/react-utils';
import modules_e5ab2127 from '../styles/RegionDialog.module.less.js';
import RegionsList from './RegionsList.js';

const getCurrentDomain = () => {
    const { hostname } = window.location;
    const domainLevels = hostname.split('.');
    const [, ...restDomainLevels] = domainLevels;
    if (domainLevels.length > 2)
        return `.${restDomainLevels.join('.')}`;
    return `.${domainLevels.join('.')}`;
};
function setRegionCookie(route) {
    const domain = getCurrentDomain();
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    document.cookie = `${"_SL_"}=${route}; domain=${domain}; path=/; expires=${date.toUTCString()}`;
}
/**
 * @description RegionDialog component.
 *
 * @component
 * @example
 * ```jsx
 * <RegionDialog />
 * ```
 */
const RegionDialog = React.forwardRef((props, ref) => {
    const { title, subtitle, icon, serviceURL, regions, formatPath, getServiceUrl, onChange, onClose, className, ...restProps } = props;
    const cx = useStyles(modules_e5ab2127);
    const [searchValue, setSearchValue] = React.useState(undefined);
    const debounceSearchValue = useDebounceValue(searchValue, 300);
    const [{ get, data, loading }] = useFetch(new URL('/suggest/v1/locations', "https://api.sravni.ru").toString());
    const setSelectedRegionAndRedirect = useCallback(async (region) => {
        let path = '';
        if (getServiceUrl) {
            path = await getServiceUrl(region);
        }
        else {
            path = formatPath ? await formatPath(region) : `${serviceURL}${region.alias}`;
        }
        setRegionCookie(region.route);
        onClose?.();
        window.location.href = path;
    }, [formatPath, getServiceUrl, onClose, serviceURL]);
    const handleChangeInput = React.useCallback((value) => {
        setSearchValue(value);
    }, []);
    const handleSelect = React.useCallback((option) => {
        onChange?.(option);
        setSelectedRegionAndRedirect(option);
    }, [onChange, setSelectedRegionAndRedirect]);
    const handleChangeSelect = React.useCallback(
    // @ts-ignore
    value => {
        if (!data) {
            return [];
        }
        const option = data.find(o => [o.id, o.name].includes(value));
        if (option) {
            onChange?.(option);
            setSelectedRegionAndRedirect(option);
        }
    }, [data, onChange, setSelectedRegionAndRedirect]);
    const options = React.useMemo(() => {
        if (!data) {
            return [];
        }
        return data.map((item) => ({ label: item.name, value: item.id }));
    }, [data]);
    React.useEffect(() => {
        if (debounceSearchValue) {
            get({ q: debounceSearchValue });
        }
    }, [debounceSearchValue, get]);
    return (React.createElement(Dialog, { ref: ref, onClose: onClose, ...restProps },
        React.createElement(Dialog.Header, { icon: icon, title: title, subtitle: subtitle }),
        React.createElement(Dialog.Content, { className: cx('content') },
            React.createElement("div", { className: cx(className, 'container') },
                React.createElement(Autocomplete, { placeholder: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0433\u043E\u0440\u043E\u0434\u0430", loading: loading, options: options, onSearch: handleChangeInput, onChange: handleChangeSelect, icon: React.createElement(Search, null) }),
                React.createElement(RegionsList, { regions: regions, onSelect: handleSelect })))));
});
RegionDialog.defaultProps = {
    title: 'Выберите город',
};

export { RegionDialog };
//# sourceMappingURL=RegionDialog.js.map
